import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Clock3, ImageOff, RefreshCw, Square, SquareCheckBig } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type PersonalFlowCard,
  type PersonalFlowCurrentRegistrationProgress,
  type PersonalFlowCurrentTrainingProgramProgress,
  createPersonalFlowSectionBudgets,
  expandPersonalFlowSectionBudget,
  selectNextPersonalFlowSectionExpansion,
  selectPersonalFlowSectionCards,
  type PersonalFlowSectionBudgets,
  type PersonalFlowSectionKey,
  usePersonalFlowSections,
  usePersonalFlowSettings,
  useSavePersonalFlowSettings,
} from '@features/personal-flow';
import { useAuth } from '@features/auth';
import { educationModuleQuery, educationModuleSlideQuery } from '@features/education-module';
import { useUserProfileContext } from '@entities/user-profile';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  StatusBadge,
  Button,
  Card,
  CardContent,
  Heading,
  Label,
  NativeSelect,
  Progress,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
} from '@shared/ui';

export type PersonalFlowTab = 'in-progress' | 'completed' | 'settings';

const RATE_LIMIT_FALLBACK = 'bubble-data-api:personal-flow-rate-limit-empty-fallback';

interface PersonalFlowPageProps {
  locale: string;
  activeTab: PersonalFlowTab;
  onTabChange: (tab: PersonalFlowTab) => void;
}

function readProgramId(program: { bubble_id?: string; id?: string; training_program_id?: string }) {
  return program.bubble_id ?? program.id ?? program.training_program_id ?? '';
}

type CompactDuration =
  | { kind: 'exact'; minutes: number }
  | { kind: 'up-to'; minutes: number }
  | { kind: 'from'; minutes: number }
  | { kind: 'range'; from: number; to: number };

function readCompactDuration(duration: string | number): CompactDuration | null {
  if (typeof duration === 'number') {
    if (!Number.isFinite(duration) || duration < 0) return null;
    return { kind: 'exact', minutes: Math.max(1, Math.ceil(duration / 60)) };
  }

  const normalized = duration.trim();
  const range = normalized.match(/^(\d+)\s*(?:-|–|to)\s*(\d+)\s*(?:minutes?|mins?)$/iu);
  if (range) {
    return { kind: 'range', from: Number(range[1]), to: Number(range[2]) };
  }

  const upTo = normalized.match(/^(?:under|up to|less than)\s+(\d+)\s*(?:minutes?|mins?)$/iu);
  if (upTo) return { kind: 'up-to', minutes: Number(upTo[1]) };

  const from = normalized.match(/^(?:over|at least|more than)\s+(\d+)\s*(?:minutes?|mins?)$/iu);
  if (from) return { kind: 'from', minutes: Number(from[1]) };

  const exact = normalized.match(/^(\d+)\s*(?:minutes?|mins?)$/iu);
  return exact ? { kind: 'exact', minutes: Number(exact[1]) } : null;
}

function readInstructorName(
  registration:
    | {
        instructor?:
          | string
          | {
              status: 'assigned' | 'not_assigned' | 'unavailable';
              display_name: string | null;
            }
          | null;
        instructor_name?: string | null;
      }
    | undefined
) {
  if (typeof registration?.instructor === 'string') return registration.instructor;
  if (registration?.instructor?.status === 'assigned') {
    return registration.instructor.display_name;
  }
  return registration?.instructor_name ?? null;
}

export function PersonalFlowPage({ locale, activeTab, onTabChange }: PersonalFlowPageProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const accountId = user?.id ?? '';
  const userContext = useUserProfileContext(accountId);
  const context = userContext.data?.data;
  const profileRef = context?.profile_ref ?? '';
  const registrationRef = context?.current_registration?.registration_id ?? '';
  const programRef = context?.current_training_program?.program_id ?? '';
  const sectionBudgetKey = `${accountId}:${profileRef}:${registrationRef}:${programRef}`;
  const initialSectionBudgets = useMemo(
    () =>
      createPersonalFlowSectionBudgets({
        hasRegistration: registrationRef.length > 0,
        hasTrainingProgram: programRef.length > 0,
      }),
    [programRef, registrationRef]
  );
  const [sectionBudgetState, setSectionBudgetState] = useState<{
    key: string;
    budgets: PersonalFlowSectionBudgets;
  }>(() => ({ key: sectionBudgetKey, budgets: initialSectionBudgets }));
  const sectionBudgets =
    sectionBudgetState.key === sectionBudgetKey
      ? sectionBudgetState.budgets
      : initialSectionBudgets;
  const sections = usePersonalFlowSections({
    accountId,
    profileRef,
    registrationRef,
    programRef,
    slidesCounts: sectionBudgets,
    enabled: activeTab !== 'settings',
    progressEnabled: activeTab === 'in-progress',
  });
  const settings = usePersonalFlowSettings(accountId, profileRef, activeTab === 'settings');
  const currentCourseProgress = sections.certificationRegistrationProgress.data?.data ?? null;
  const cardsForActiveTab = useCallback(
    (query: PersonalFlowSectionQuery) =>
      activeTab === 'completed'
        ? (query.data?.data.payload.slides_completed?.slides ?? [])
        : (query.data?.data.payload.slides_progress?.slides ?? []),
    [activeTab]
  );
  const sectionExpansionCandidates = [
    ...(registrationRef.length > 0
      ? [
          {
            key: 'certification-registration' as const,
            query: sections.certificationRegistration,
          },
        ]
      : []),
    ...(programRef.length > 0
      ? [{ key: 'training-program' as const, query: sections.trainingProgram }]
      : []),
    { key: 'general' as const, query: sections.general },
  ].map(({ key, query }) => ({
    key,
    cards: cardsForActiveTab(query),
    isReady:
      !query.isFetching &&
      !query.isPlaceholderData &&
      (query.isError ||
        (query.data !== undefined &&
          query.data.meta?.sourceDetail !== RATE_LIMIT_FALLBACK &&
          !(
            query.data.meta?.cache?.status === 'warming' &&
            query.data.meta.cache.isComplete === false
          ))),
  }));
  const nextSectionExpansion =
    activeTab === 'settings'
      ? null
      : selectNextPersonalFlowSectionExpansion({
          sections: sectionExpansionCandidates,
          budgets: sectionBudgets,
        });
  const isSectionUnionSettled =
    activeTab !== 'settings' &&
    nextSectionExpansion === null &&
    sectionExpansionCandidates.every(({ isReady }) => isReady);
  const prefetchScope = `${sectionBudgetKey}:${activeTab}`;
  const settledPreloadCandidate = isSectionUnionSettled
    ? selectPersonalFlowSectionCards(sectionExpansionCandidates)
        .flatMap(({ cards }) => cards)
        .find((card) => card.target?.kind === 'education_module')?.slide_id
    : undefined;
  const [idlePreloadState, setIdlePreloadState] = useState<{
    scope: string;
    cardId: string | undefined;
    locked: boolean;
  }>(() => ({ scope: prefetchScope, cardId: undefined, locked: false }));
  const automaticPreloadCardId =
    isSectionUnionSettled && idlePreloadState.scope === prefetchScope && idlePreloadState.locked
      ? idlePreloadState.cardId
      : undefined;

  useEffect(() => {
    setSectionBudgetState((current) =>
      current.key === sectionBudgetKey
        ? current
        : { key: sectionBudgetKey, budgets: initialSectionBudgets }
    );
  }, [initialSectionBudgets, sectionBudgetKey]);

  useEffect(() => {
    if (nextSectionExpansion === null) return;
    setSectionBudgetState((current) => {
      const budgets = current.key === sectionBudgetKey ? current.budgets : initialSectionBudgets;
      return {
        key: sectionBudgetKey,
        budgets: expandPersonalFlowSectionBudget(budgets, nextSectionExpansion),
      };
    });
  }, [initialSectionBudgets, nextSectionExpansion, sectionBudgetKey]);

  useEffect(() => {
    setIdlePreloadState((current) => {
      if (current.scope !== prefetchScope) {
        return {
          scope: prefetchScope,
          cardId: isSectionUnionSettled ? settledPreloadCandidate : undefined,
          locked: isSectionUnionSettled && settledPreloadCandidate !== undefined,
        };
      }
      if (!current.locked && isSectionUnionSettled && settledPreloadCandidate !== undefined) {
        return { ...current, cardId: settledPreloadCandidate, locked: true };
      }
      return current;
    });
  }, [isSectionUnionSettled, prefetchScope, settledPreloadCandidate]);

  return (
    <section className="mx-auto w-full max-w-6xl space-y-6" aria-label={t('personalFlow.title')}>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          onTabChange(value as PersonalFlowTab);
        }}
      >
        <TabsList
          variant="primary"
          aria-label={t('personalFlow.tabs.label')}
          className="w-full min-w-0 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible"
        >
          <TabsTrigger
            variant="primary"
            value="in-progress"
            className="min-h-11 shrink-0 whitespace-normal px-4 py-2 text-left text-base sm:text-center"
          >
            {t('personalFlow.tabs.inProgress')}
          </TabsTrigger>
          <TabsTrigger
            variant="primary"
            value="completed"
            className="min-h-11 shrink-0 whitespace-normal px-4 py-2 text-left text-base sm:text-center"
          >
            {t('personalFlow.tabs.completed')}
          </TabsTrigger>
          <TabsTrigger
            variant="primary"
            value="settings"
            className="min-h-11 shrink-0 whitespace-normal px-4 py-2 text-left text-base sm:text-center"
          >
            {t('personalFlow.tabs.settings')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="in-progress" className="mt-6">
          <div className="space-y-10">
            {registrationRef.length > 0 ? (
              <ProgressSummaryState
                isLoading={sections.certificationRegistrationProgress.isLoading}
                isFetching={sections.certificationRegistrationProgress.isFetching}
                isError={sections.certificationRegistrationProgress.isError}
                isUnavailable={currentCourseProgress === null}
                loadingLabel={t('personalFlow.courseProgress.loading')}
                errorTitle={t('personalFlow.courseProgress.errorTitle')}
                errorDescription={t('personalFlow.courseProgress.errorDescription')}
                unavailableDescription={t('personalFlow.courseProgress.unavailableDescription')}
                onRetry={() => void sections.certificationRegistrationProgress.refetch()}
              >
                {currentCourseProgress ? (
                  <CurrentCourseProgressCard
                    progress={currentCourseProgress}
                    onSwitchRegistration={() => {
                      onTabChange('settings');
                    }}
                  />
                ) : null}
              </ProgressSummaryState>
            ) : null}
            <ProgressiveFeedPanel
              userContext={userContext}
              sections={sections}
              registrationRef={registrationRef}
              programRef={programRef}
              locale={locale}
              state="in-progress"
              preloadCardId={automaticPreloadCardId}
              onSwitchTrainingProgram={() => {
                onTabChange('settings');
              }}
            />
          </div>
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          <ProgressiveFeedPanel
            userContext={userContext}
            sections={sections}
            registrationRef={registrationRef}
            programRef={programRef}
            locale={locale}
            state="completed"
            preloadCardId={automaticPreloadCardId}
            onSwitchTrainingProgram={() => {
              onTabChange('settings');
            }}
          />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <SettingsPanel accountId={accountId} profileRef={profileRef} settings={settings} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

type PersonalFlowSections = ReturnType<typeof usePersonalFlowSections>;
type PersonalFlowSectionQuery = PersonalFlowSections['certificationRegistration'];
type UserContextQuery = ReturnType<typeof useUserProfileContext>;

function ProgressiveFeedPanel({
  userContext,
  sections,
  registrationRef,
  programRef,
  locale,
  state,
  preloadCardId,
  onSwitchTrainingProgram,
}: {
  userContext: UserContextQuery;
  sections: PersonalFlowSections;
  registrationRef: string;
  programRef: string;
  locale: string;
  state: 'in-progress' | 'completed';
  preloadCardId?: string;
  onSwitchTrainingProgram: () => void;
}) {
  const { t } = useTranslation();

  if (userContext.isLoading) return <FeedSkeleton />;
  if (userContext.isError) {
    return (
      <StateAlert
        title={t('personalFlow.states.errorTitle')}
        description={t('personalFlow.states.errorDescription')}
        onRetry={() => void userContext.refetch()}
      />
    );
  }

  const activeSections: Array<{ key: PersonalFlowSectionKey; query: PersonalFlowSectionQuery }> = [
    ...(registrationRef.length > 0
      ? [
          {
            key: 'certification-registration' as const,
            query: sections.certificationRegistration,
          },
        ]
      : []),
    ...(programRef.length > 0
      ? [{ key: 'training-program' as const, query: sections.trainingProgram }]
      : []),
    { key: 'general' as const, query: sections.general },
  ];
  const cardsFor = (query: PersonalFlowSectionQuery) =>
    state === 'in-progress'
      ? (query.data?.data.payload.slides_progress?.slides ?? [])
      : (query.data?.data.payload.slides_completed?.slides ?? []);
  const selectedSections = selectPersonalFlowSectionCards(
    activeSections.map(({ key, query }) => ({ key, cards: cardsFor(query) }))
  );
  const visibleSections = activeSections.map(({ key, query }) => ({
    key,
    query,
    cards: selectedSections.find((section) => section.key === key)?.cards ?? [],
  }));
  const hasCards = visibleSections.some(({ cards }) => cards.length > 0);
  const trainingProgramProgress = sections.trainingProgramProgress.data?.data ?? null;
  const showTrainingProgramProgress = state === 'in-progress' && programRef.length > 0;
  const isPending = activeSections.some(({ query }) => query.isLoading);
  const hasError = activeSections.some(({ query }) => query.isError);
  const hasPartialFailure = (hasCards || trainingProgramProgress !== null) && hasError;
  const hasDeferredState = activeSections.some(
    ({ query }) =>
      (query.data?.meta?.cache?.status === 'warming' &&
        query.data.meta.cache.isComplete === false) ||
      query.data?.meta?.sourceDetail === RATE_LIMIT_FALLBACK
  );
  const showEmptyFeed = !hasCards && !isPending && !hasError && !hasDeferredState;

  if (showEmptyFeed && !showTrainingProgramProgress) {
    return <EmptyFeedState state={state} />;
  }

  return (
    <div className="space-y-6">
      {visibleSections.map(({ key, query, cards }) => (
        <div key={key} className="space-y-6">
          {key === 'training-program' && showTrainingProgramProgress ? (
            <ProgressSummaryState
              isLoading={sections.trainingProgramProgress.isLoading}
              isFetching={sections.trainingProgramProgress.isFetching}
              isError={sections.trainingProgramProgress.isError}
              isUnavailable={trainingProgramProgress === null}
              loadingLabel={t('personalFlow.trainingProgramProgress.loading')}
              errorTitle={t('personalFlow.trainingProgramProgress.errorTitle')}
              errorDescription={t('personalFlow.trainingProgramProgress.errorDescription')}
              unavailableDescription={t(
                'personalFlow.trainingProgramProgress.unavailableDescription'
              )}
              onRetry={() => void sections.trainingProgramProgress.refetch()}
            >
              {trainingProgramProgress ? (
                <CurrentTrainingProgramProgressCard
                  progress={trainingProgramProgress}
                  onSwitchTrainingProgram={onSwitchTrainingProgram}
                />
              ) : null}
            </ProgressSummaryState>
          ) : null}
          <FeedPanel
            cards={cards}
            locale={locale}
            state={state}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            isError={query.isError}
            isPartialFailure={hasPartialFailure}
            isWarming={
              query.data?.meta?.cache?.status === 'warming' &&
              query.data.meta.cache.isComplete === false
            }
            isRateLimitedFallback={query.data?.meta?.sourceDetail === RATE_LIMIT_FALLBACK}
            preloadCardId={preloadCardId}
            showEmptyState={false}
            onRetry={() => void query.refetch()}
          />
        </div>
      ))}
      {showEmptyFeed ? <EmptyFeedState state={state} /> : null}
    </div>
  );
}

function CurrentCourseProgressCard({
  progress,
  onSwitchRegistration,
}: {
  progress: PersonalFlowCurrentRegistrationProgress;
  onSwitchRegistration: () => void;
}) {
  const { t } = useTranslation();
  const courseType = progress.title ?? progress.registration_id;

  return (
    <LearningProgressCard
      label={t('personalFlow.courseProgress.label', { course: courseType })}
      title={courseType}
      secondaryText={
        progress.registration_number
          ? t('personalFlow.courseProgress.batch', { batch: progress.registration_number })
          : null
      }
      percent={progress.percent}
      stepsLabel={t('personalFlow.courseProgress.steps', {
        completed: progress.completed_steps,
        total: progress.total_steps,
      })}
      progressLabel={t('personalFlow.courseProgress.progressLabel', {
        course: courseType,
        value: progress.percent,
      })}
      imageUrl={progress.course_type_image ?? null}
      imageFit="contain"
      action={{
        label: t('personalFlow.courseProgress.switchRegistration'),
        ariaLabel: t('personalFlow.courseProgress.switchRegistrationLabel'),
        onClick: onSwitchRegistration,
      }}
    />
  );
}

function ProgressSummaryState({
  isLoading,
  isFetching,
  isError,
  isUnavailable,
  loadingLabel,
  errorTitle,
  errorDescription,
  unavailableDescription,
  onRetry,
  children,
}: {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isUnavailable: boolean;
  loadingLabel: string;
  errorTitle: string;
  errorDescription: string;
  unavailableDescription: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="space-y-3 border border-border p-4" role="status" aria-label={loadingLabel}>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-2 w-full" />
        <span className="sr-only">{loadingLabel}</span>
      </div>
    );
  }

  if (isError || isUnavailable) {
    return (
      <StateAlert
        title={errorTitle}
        description={isError ? errorDescription : unavailableDescription}
        onRetry={onRetry}
        busy={isFetching}
      />
    );
  }

  return children;
}

function CurrentTrainingProgramProgressCard({
  progress,
  onSwitchTrainingProgram,
}: {
  progress: PersonalFlowCurrentTrainingProgramProgress;
  onSwitchTrainingProgram: () => void;
}) {
  const { t } = useTranslation();
  const title = progress.title ?? progress.training_program_id;

  return (
    <LearningProgressCard
      label={t('personalFlow.trainingProgramProgress.label', { program: title })}
      title={title}
      percent={progress.percent}
      stepsLabel={t('personalFlow.trainingProgramProgress.steps', {
        completed: progress.completed_steps,
        total: progress.total_steps,
      })}
      progressLabel={t('personalFlow.trainingProgramProgress.progressLabel', {
        program: title,
        value: progress.percent,
      })}
      imageUrl={progress.thumbnail ?? null}
      imageFit="cover"
      action={{
        label: t('personalFlow.trainingProgramProgress.switchProgram'),
        ariaLabel: t('personalFlow.trainingProgramProgress.switchProgramLabel'),
        onClick: onSwitchTrainingProgram,
      }}
    />
  );
}

function LearningProgressCard({
  action,
  imageFit,
  imageUrl,
  label,
  percent,
  progressLabel,
  secondaryText,
  stepsLabel,
  title,
}: {
  action?: { label: string; ariaLabel: string; onClick: () => void };
  imageFit: 'contain' | 'cover';
  imageUrl: string | null;
  label: string;
  percent: number;
  progressLabel: string;
  secondaryText?: string | null;
  stepsLabel: string;
  title: string;
}) {
  return (
    <Card aria-label={label}>
      <CardContent className="flex items-center gap-4 p-4 sm:p-5">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <Text className="font-semibold text-card-foreground">
              <span>{title}</span>
              {secondaryText ? (
                <span className="font-normal text-muted-foreground">
                  {' · '}
                  {secondaryText}
                </span>
              ) : null}
            </Text>
            {action ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto min-h-0 p-0 text-sm font-normal"
                aria-label={action.ariaLabel}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ) : null}
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <Text size="sm" color="muted">
              {stepsLabel}
            </Text>
            <Text size="sm" className="shrink-0 font-semibold text-card-foreground">
              {percent}%
            </Text>
          </div>
          <Progress value={percent} className="h-2" aria-label={progressLabel} />
        </div>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className={
              imageFit === 'cover'
                ? 'h-16 w-16 shrink-0 rounded-sm object-cover sm:h-20 sm:w-20'
                : 'h-16 w-16 shrink-0 object-contain'
            }
            decoding="async"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function FeedPanel({
  cards,
  locale,
  state,
  isLoading,
  isFetching,
  isError,
  isPartialFailure = false,
  isWarming,
  isRateLimitedFallback,
  preloadCardId,
  showEmptyState = true,
  onRetry,
}: {
  cards: PersonalFlowCard[];
  locale: string;
  state: 'in-progress' | 'completed';
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isPartialFailure?: boolean;
  isWarming: boolean;
  isRateLimitedFallback: boolean;
  preloadCardId?: string;
  showEmptyState?: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <FeedSkeleton />;
  }

  if (isError) {
    return (
      <StateAlert
        title={t(
          isPartialFailure
            ? 'personalFlow.states.partialErrorTitle'
            : 'personalFlow.states.errorTitle'
        )}
        description={t(
          isPartialFailure
            ? 'personalFlow.states.partialErrorDescription'
            : 'personalFlow.states.errorDescription'
        )}
        onRetry={onRetry}
      />
    );
  }

  if (isWarming || isRateLimitedFallback) {
    return (
      <StateAlert
        title={t(
          isWarming
            ? 'personalFlow.states.preparingTitle'
            : 'personalFlow.states.temporarilyUnavailableTitle'
        )}
        description={t(
          isWarming
            ? 'personalFlow.states.preparingDescription'
            : 'personalFlow.states.temporarilyUnavailableDescription'
        )}
        onRetry={onRetry}
        busy={isFetching}
      />
    );
  }

  if (cards.length === 0) {
    return showEmptyState ? <EmptyFeedState state={state} /> : null;
  }

  return (
    <div className="space-y-4">
      <ul className="list-none space-y-4 p-0" aria-label={t('personalFlow.listLabel')}>
        {cards.map((card) => (
          <li key={`${card.slide_id}-${card.slide_log_id ?? card.bubble_id}`}>
            <PersonalFlowCardRow
              card={card}
              locale={locale}
              state={state}
              preload={card.slide_id === preloadCardId}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyFeedState({ state }: { state: 'in-progress' | 'completed' }) {
  const { t } = useTranslation();
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 text-center">
        <Heading as="h2" size="lg">
          {t(
            state === 'in-progress'
              ? 'personalFlow.states.inProgressEmptyTitle'
              : 'personalFlow.states.completedEmptyTitle'
          )}
        </Heading>
        <Text color="muted" className="mt-2">
          {t(
            state === 'in-progress'
              ? 'personalFlow.states.inProgressEmptyDescription'
              : 'personalFlow.states.completedEmptyDescription'
          )}
        </Text>
      </CardContent>
    </Card>
  );
}

function PersonalFlowCardRow({
  card,
  locale,
  state,
  preload,
}: {
  card: PersonalFlowCard;
  locale: string;
  state: 'in-progress' | 'completed';
  preload: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const context = card.registration_title ?? card.program_title ?? card.program ?? card.workout;
  const activityMode = state === 'completed' ? 'review' : 'resume';
  const navigation = card.target?.kind === 'education_module' ? card.target : null;
  const initialSlideId = navigation?.initial_slide_id ?? card.slide_id;
  const compactDuration =
    card.duration !== null && card.duration !== undefined
      ? readCompactDuration(card.duration)
      : null;
  const durationLabel =
    compactDuration?.kind === 'exact'
      ? t('personalFlow.card.durationMinutes', { minutes: compactDuration.minutes })
      : compactDuration?.kind === 'up-to'
        ? t('personalFlow.card.durationUpToMinutes', { minutes: compactDuration.minutes })
        : compactDuration?.kind === 'from'
          ? t('personalFlow.card.durationFromMinutes', { minutes: compactDuration.minutes })
          : compactDuration?.kind === 'range'
            ? t('personalFlow.card.durationRangeMinutes', {
                from: compactDuration.from,
                to: compactDuration.to,
              })
            : card.duration !== null && card.duration !== undefined
              ? String(card.duration)
              : null;

  const prefetchTarget = useCallback(() => {
    if (navigation === null) return;
    void Promise.allSettled([
      queryClient.prefetchQuery(educationModuleQuery(navigation, locale)),
      queryClient.prefetchQuery(educationModuleSlideQuery(navigation, initialSlideId, locale)),
    ]);
  }, [initialSlideId, locale, navigation, queryClient]);

  useEffect(() => {
    if (!preload || navigation === null) return;
    if (typeof window.requestIdleCallback === 'function') {
      const callbackId = window.requestIdleCallback(prefetchTarget, { timeout: 1_500 });
      return () => {
        window.cancelIdleCallback(callbackId);
      };
    }
    const timeoutId = window.setTimeout(prefetchTarget, 0);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [navigation, prefetchTarget, preload]);

  return (
    <Card
      className="overflow-hidden transition-shadow hover:shadow-md"
      onPointerEnter={prefetchTarget}
      onFocusCapture={prefetchTarget}
    >
      <CardContent className="flex min-h-28 gap-0 p-0 sm:min-h-36">
        <div className="relative w-32 shrink-0 bg-muted sm:w-64">
          {card.image ? (
            <img src={card.image} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <ImageOff aria-hidden="true" className="h-7 w-7" />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-3 p-3 sm:p-4">
          <div className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <Heading as="h2" size="md" className="min-w-0">
                  {card.title ?? t('personalFlow.card.untitled')}
                </Heading>
                {durationLabel ? (
                  <Badge
                    appearance="outline"
                    className="shrink-0 font-normal text-muted-foreground"
                    icon={<Clock3 className="h-3.5 w-3.5" />}
                  >
                    {durationLabel}
                  </Badge>
                ) : null}
              </div>
              <StatusBadge
                className="shrink-0"
                icon={
                  state === 'completed' ? (
                    <SquareCheckBig className="h-3.5 w-3.5" />
                  ) : (
                    <Square className="h-3.5 w-3.5" />
                  )
                }
                tone={state === 'completed' ? 'positive' : 'informational'}
              >
                {t(
                  state === 'completed'
                    ? 'personalFlow.card.completed'
                    : 'personalFlow.card.inProgress'
                )}
              </StatusBadge>
            </div>
            {context ? (
              <Text size="sm" color="muted" className="line-clamp-2">
                {context}
              </Text>
            ) : null}
          </div>
          {navigation !== null ? (
            <Button asChild className="min-h-11 self-start">
              <Link
                to="/$locale/student/education/modules/$moduleId"
                params={{ locale, moduleId: navigation.module_id }}
                search={{
                  profileRef: navigation.profile_ref,
                  contextKind: navigation.context.kind,
                  contextId: navigation.context.id,
                  mode: activityMode,
                  slideId: navigation.initial_slide_id ?? card.slide_id,
                  emLogId: navigation.em_log_id,
                }}
              >
                {t(
                  state === 'completed' ? 'personalFlow.card.review' : 'personalFlow.card.continue'
                )}
              </Link>
            </Button>
          ) : (
            <div role="status" className="space-y-1">
              <Text size="sm" className="font-medium">
                {t('personalFlow.card.contentUnavailable')}
              </Text>
              <Text size="xs" color="muted">
                {t('personalFlow.card.contentUnavailableDescription')}
              </Text>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsPanel({
  accountId,
  profileRef,
  settings,
}: {
  accountId: string;
  profileRef: string;
  settings: ReturnType<typeof usePersonalFlowSettings>;
}) {
  const { t } = useTranslation();
  const { context, programs } = settings;
  const save = useSavePersonalFlowSettings(accountId, profileRef);
  const contextData = context.data?.data;
  const registrationOptions = useMemo(
    () => contextData?.registrations ?? [],
    [contextData?.registrations]
  );
  const programOptions = programs.data?.data.payload.bt_program ?? [];
  const confirmedRegistration = contextData?.current_registration?.registration_id ?? '';
  const confirmedProgram = contextData?.current_training_program?.program_id ?? '';
  const [registrationId, setRegistrationId] = useState('');
  const [programId, setProgramId] = useState('');

  useEffect(() => {
    setRegistrationId(confirmedRegistration);
  }, [confirmedRegistration]);
  useEffect(() => {
    setProgramId(confirmedProgram);
  }, [confirmedProgram]);

  const currentRegistration = useMemo(
    () => registrationOptions.find((item) => item.registration_id === confirmedRegistration),
    [confirmedRegistration, registrationOptions]
  );
  const instructorName = readInstructorName(currentRegistration);
  const registrationChanged = registrationId !== confirmedRegistration;
  const programChanged = programId !== confirmedProgram;
  const canSave =
    !save.isPending &&
    ((registrationChanged && registrationId.length > 0) ||
      (programChanged && programId.length > 0));

  if (context.isLoading || programs.isLoading) {
    return <FeedSkeleton />;
  }

  if (context.isError || programs.isError) {
    return (
      <StateAlert
        title={t('personalFlow.settings.errorTitle')}
        description={t('personalFlow.settings.errorDescription')}
        onRetry={() => {
          void Promise.all([context.refetch(), programs.refetch()]);
        }}
      />
    );
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-5 sm:p-7">
        <div>
          <Heading as="h2" size="lg">
            {t('personalFlow.settings.title')}
          </Heading>
          <Text color="muted" className="mt-1">
            {t('personalFlow.settings.description')}
          </Text>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="personal-flow-registration">
              {t('personalFlow.settings.currentRegistration')}
            </Label>
            <NativeSelect
              id="personal-flow-registration"
              value={registrationId}
              onChange={(event) => {
                setRegistrationId(event.target.value);
              }}
              disabled={registrationOptions.length === 0 || save.isPending}
            >
              <option value="">{t('personalFlow.settings.noRegistration')}</option>
              {registrationOptions.map((registration) => {
                const id = registration.registration_id;
                const number = registration.number;
                const title = registration.title ?? id;
                return (
                  <option key={id} value={id}>
                    {number ? `${title} · ${number}` : title}
                  </option>
                );
              })}
            </NativeSelect>
            {currentRegistration ? (
              <Text size="sm" color="muted">
                {t('personalFlow.settings.registrationNumber', {
                  number: currentRegistration.number ?? t('personalFlow.settings.unavailable'),
                })}
              </Text>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="personal-flow-program">
              {t('personalFlow.settings.currentProgram')}
            </Label>
            <NativeSelect
              id="personal-flow-program"
              value={programId}
              onChange={(event) => {
                setProgramId(event.target.value);
              }}
              disabled={programOptions.length === 0 || save.isPending}
            >
              <option value="">{t('personalFlow.settings.noProgram')}</option>
              {programOptions.map((program) => {
                const id = readProgramId(program);
                return id ? (
                  <option key={id} value={id}>
                    {program.title ?? id}
                  </option>
                ) : null;
              })}
            </NativeSelect>
          </div>
        </div>

        <div className="space-y-1">
          <Text size="sm" className="font-medium">
            {t('personalFlow.settings.instructor')}
          </Text>
          <Text color="muted">
            {instructorName ?? t('personalFlow.settings.instructorUnavailable')}
          </Text>
        </div>

        {save.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertCircle aria-hidden="true" className="h-4 w-4" />
            <AlertTitle>{t('personalFlow.settings.saveErrorTitle')}</AlertTitle>
            <AlertDescription>{t('personalFlow.settings.saveErrorDescription')}</AlertDescription>
          </Alert>
        ) : null}
        {save.isSuccess && !registrationChanged && !programChanged ? (
          <Text role="status" aria-live="polite" className="text-success">
            {t('personalFlow.settings.saved')}
          </Text>
        ) : null}

        <Button
          type="button"
          className="min-h-11 w-full sm:w-auto sm:min-w-52"
          disabled={!canSave}
          onClick={() => {
            save.mutate({
              ...(registrationChanged ? { currentRegistrationId: registrationId } : {}),
              ...(programChanged ? { mainProgramId: programId } : {}),
            });
          }}
        >
          {save.isPending ? t('personalFlow.settings.applying') : t('personalFlow.settings.apply')}
        </Button>
      </CardContent>
    </Card>
  );
}

function StateAlert({
  title,
  description,
  onRetry,
  busy = false,
}: {
  title: string;
  description: string;
  onRetry: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Alert role="status" aria-live="polite">
      <AlertCircle aria-hidden="true" className="h-4 w-4" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="space-y-4">
        <p>{description}</p>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={onRetry}
          disabled={busy}
        >
          <RefreshCw
            aria-hidden="true"
            className={busy ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'}
          />
          {t('personalFlow.states.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function FeedSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" role="status" aria-label={t('personalFlow.states.loading')}>
      {[0, 1, 2].map((item) => (
        <div key={item} className="flex overflow-hidden border border-border">
          <Skeleton className="aspect-video w-40 shrink-0 self-start rounded-none sm:w-48" />
          <div className="flex-1 space-y-3 p-4">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-11 w-full" />
          </div>
        </div>
      ))}
      <span className="sr-only">{t('personalFlow.states.loading')}</span>
    </div>
  );
}
